// App shell: wires the SessionStore to the component tree and routes user
// intent through the client. All state lives in the store; this component
// only selects the action kind (a pure UI concern) and forwards callbacks.

import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { Play, FileText, Cpu, Moon, Sun, Feather, Disc, RefreshCw, Scan, Download, Flag, Plus, Grid2X2, Settings2 } from "lucide-react";

import { actionIdOf, toCanvas, PASS_ACTION_ID, type ActionKind } from "../core/codec.js";
import type { SessionStore } from "../core/store.js";
import { Board } from "./Board.js";
import {
  AnalysisPanel,
  KindSelector,
  QuotaBar,
  ReceiptLog,
  ReplayBar,
  SgfPanel,
  StatusBar,
} from "./panels.js";
import type { SgfMove } from "../core/sgf.js";
import type { ModelEndpoint } from "../core/model-directory.js";

// The interaction surface App needs; GatewayClient implements it, tests stub
// it. All commands take the actor from the store's tip at call time.
export interface AppActions {
  connect(): void;
  submitAction(actor: "BLACK" | "WHITE", actionId: number, kind: ActionKind | "PASS"): string;
  submitTermination(actor: "BLACK" | "WHITE", kind: "RESIGNATION" | "TIMEOUT"): string;
  requestSnapshot(): void;
  requestLegalMask(): void;
  requestExportLog(): void;
  analysisStart(maxTotalPlayouts: number, maxDurationSeconds: number): void;
  analysisStop(): void;
  controlModel?(enabled: boolean | null): Promise<boolean>;
  // SGF import: hands parsed mainline intents to a fresh session (M6-D4).
  startImport(moves: SgfMove[]): void;
}

export interface AppProps {
  store: SessionStore;
  actions: AppActions;
  models?: readonly ModelEndpoint[];
  currentModelId?: string;
  onModelSelect?: (model: ModelEndpoint) => void;
  onNewGame?: () => void;
}

export function currentAnalysisChoice(store: SessionStore) {
  const tip = store.tip;
  const analysis = store.analysis;
  if (store.broken !== null || store.phase !== "live" || store.baselinePending || store.isReplay ||
      store.pending.size !== 0 || tip === null || tip.state.terminalState !== null ||
      (tip.state.actor !== "BLACK" && tip.state.actor !== "WHITE") ||
      analysis === null || analysis.chosenAction === null ||
      analysis.basisRevision !== tip.revision || analysis.basisSemanticEventCursor !== tip.semanticEventCursor)
    return null;
  return { actor: tip.state.actor, action: analysis.chosenAction };
}

export function playAnalysisChoice(store: SessionStore, actions: AppActions): boolean {
  // Recheck at click time, including pending commands from a prior click.
  const choice = currentAnalysisChoice(store);
  if (choice === null) return false;
  actions.analysisStop();
  actions.submitAction(choice.actor, choice.action.actionId, choice.action.kind as ActionKind | "PASS");
  return true;
}

export function App(props: AppProps) {
  const { store, actions } = props;
  useSyncExternalStore(
    (listener) => store.subscribe(listener),
    () => store.changeCounter,
    () => store.changeCounter, // server snapshot (renderToString tests)
  );
  const [kind, setKind] = useState<ActionKind>("NORMAL");
  const [theme, setTheme] = useState("light");
  const [tab, setTab] = useState("play");
  const [coordinates, setCoordinates] = useState(true);
  const [autoAnalysis, setAutoAnalysis] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [modelBusy, setModelBusy] = useState(false);
  const [modelTarget, setModelTarget] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const completed = useRef<typeof store.analysis>(null);
  const [playouts, setPlayouts] = useState(65536);
  useEffect(() => {
    if (store.phase !== "live" || !actions.controlModel) { setModelReady(false); return; }
    let cancelled = false;
    actions.controlModel(null).then((ready) => { if (!cancelled) setModelReady(ready); })
      .catch((error) => { if (!cancelled) setModelError(String(error)); });
    return () => { cancelled = true; };
  }, [store.phase, actions]);
  const toggleModel = async (enabled: boolean) => {
    if (!actions.controlModel || modelBusy) return;
    setModelTarget(enabled); setModelBusy(true); setModelError(null);
    if (!enabled) { setAutoAnalysis(false); actions.analysisStop(); }
    try { setModelReady(await actions.controlModel(enabled)); }
    catch (error) { setModelReady(false); setModelError(String(error)); }
    finally { setModelBusy(false); }
  };
  useEffect(() => {
    try { const saved = localStorage.getItem("mutago.theme");
      if (["light", "dark", "elegant", "minimal", "cyber"].includes(saved ?? "")) setTheme(saved!);
    } catch { /* Theme persistence is optional. */ }
  }, []);
  const changeTheme = (value: string) => { setTheme(value); try { localStorage.setItem("mutago.theme", value); } catch {} };

  const view = store.view;
  const interactive =
    store.broken === null &&
    store.phase === "live" &&
    !store.baselinePending &&
    !store.isReplay &&
    store.pending.size === 0 &&
    view !== null &&
    view.state.phase !== "TERMINAL" &&
    view.state.terminalState === null &&
    (view.state.actor === "BLACK" || view.state.actor === "WHITE");
  const pendingDouble = view?.state.pendingDouble ?? null;
  const remaining = view !== null && (view.state.actor === "BLACK" || view.state.actor === "WHITE")
    ? view.state.quotas.remaining[view.state.actor] : undefined;
  const selectedKind = pendingDouble !== null || (kind !== "NORMAL" && remaining?.[kind] === 0) ? "NORMAL" : kind;
  useEffect(() => { if (pendingDouble !== null) setKind("NORMAL"); }, [pendingDouble]);
  useEffect(() => {
    if (!autoAnalysis || !modelReady || modelBusy || !interactive) return;
    actions.analysisStart(playouts, 300);
    const timer = setTimeout(() => setRound((value) => value + 1), 301000);
    return () => {
      clearTimeout(timer);
      if (store.phase === "live" || store.phase === "syncing") actions.analysisStop();
    };
  }, [autoAnalysis, modelReady, modelBusy, interactive, view?.revision, playouts, round, actions]);
  useEffect(() => {
    const update = store.analysis;
    if (autoAnalysis && modelReady && interactive && update !== null && update !== completed.current &&
        update.basisRevision === view?.revision && update.playoutsCompleted >= playouts) {
      completed.current = update;
      setRound((value) => value + 1);
    }
  }, [store.analysis, autoAnalysis, modelReady, interactive, view?.revision, playouts]);

  const actor = view !== null && (view.state.actor === "BLACK" || view.state.actor === "WHITE")
    ? view.state.actor
    : null;

  // Read-model requests need an open socket; a disconnected click would
  // throw ClientClosedError out of the handler.
  const connected =
    store.broken === null &&
    (store.phase === "live" || store.phase === "syncing" || store.phase === "recovering");

  const onPoint = (x: number, y: number) => {
    if (!interactive || view === null || actor === null) return;
    const canvas = toCanvas(view.state.boardSize, x, y);
    actions.submitAction(actor, actionIdOf(selectedKind, canvas.x, canvas.y), selectedKind);
  };
  const onPass = () => {
    if (!interactive || actor === null) return;
    actions.submitAction(actor, PASS_ACTION_ID, "PASS");
  };
  const onResign = () => {
    if (!interactive || actor === null) return;
    actions.submitTermination(actor, "RESIGNATION");
  };

  return (
    <div className="app" data-theme={theme} data-tab={tab}>
      <nav className="navigation-rail" aria-label="工作区">
        <div className="brand-seal">弈</div>
        <div className="workspace-tabs">
          {[{ id: "play", label: "对弈", Icon: Play }, { id: "records", label: "棋谱", Icon: FileText },
            { id: "analysis", label: "分析", Icon: Cpu }].map(({ id, label, Icon }) =>
            <button key={id} title={label} aria-label={label + "工作区"} aria-pressed={tab === id} onClick={() => setTab(id)}><Icon size={22} /><span>{label}</span></button>)}
        </div>
        <button className="theme-shortcut" title="切换明暗主题" aria-label="切换明暗主题" onClick={() => changeTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? <Sun size={21} /> : <Moon size={21} />}
        </button>
      </nav>
      <div className="workspace">
      <header>
        <div className="brand-title"><h1>MutaGo</h1><span>坍缩围棋</span></div>
        {props.models !== undefined && props.models.length > 0 && (
          <label className="model-selector">
            <span>模型</span>
            <select aria-label="对弈模型" value={props.currentModelId ?? ""} disabled={store.pending.size > 0}
              onChange={(event) => {
                if (store.pending.size > 0) return;
                const model = props.models?.find((entry) => entry.id === event.target.value);
                if (model !== undefined) props.onModelSelect?.(model);
              }}>
              <option value="" disabled>当前服务</option>
              {props.models.map((model) => (
                <option key={model.id} value={model.id}>{model.label}</option>
              ))}
            </select>
          </label>
        )}
        <div className="conn-controls">
          <button onClick={() => actions.connect()} title="重新连接" aria-label="重新连接">
            <RefreshCw size={18} />
          </button>
          <button onClick={() => actions.requestSnapshot()} disabled={!connected} title="快照" aria-label="快照"><Scan size={18} /></button>
          <button onClick={() => actions.requestLegalMask()} disabled={!connected} title="合法集" aria-label="合法集"><Grid2X2 size={18} /></button>
          <button onClick={() => actions.requestExportLog()} disabled={!connected} title="导出 SGF" aria-label="导出 SGF"><Download size={18} /></button>
          <button onClick={onResign} disabled={!interactive} title="认输" aria-label="认输">
            <Flag size={18} />
          </button>
        </div>
      </header>
      <StatusBar store={store} />
      <main>
        <section className="board-column">
          <div className="players-strip">
            <div className={actor === "BLACK" ? "player active" : "player"}><i className="player-stone black" /><span>黑方</span><strong>{view?.state.quotas.remaining.BLACK.IMMORTAL ?? 1}<small> / {view?.state.quotas.remaining.BLACK.DOUBLE_START ?? 1} / {view?.state.quotas.remaining.BLACK.EIGHTWAY ?? 1}</small></strong></div>
            <div className={actor === "WHITE" ? "player active" : "player"}><i className="player-stone white" /><span>白方</span><strong>{view?.state.quotas.remaining.WHITE.IMMORTAL ?? 1}<small> / {view?.state.quotas.remaining.WHITE.DOUBLE_START ?? 1} / {view?.state.quotas.remaining.WHITE.EIGHTWAY ?? 1}</small></strong></div>
            <div className="move-count"><span>REV</span><strong>{view?.revision ?? 0}</strong></div>
          </div>
          {pendingDouble !== null && <div className="double-notice" role="status"><strong>skip</strong><span>{pendingDouble.owner === "BLACK" ? "黑方" : "白方"}继续落下第二手</span></div>}
        {view !== null ? (
          <Board
            state={view.state}
            legalMask={store.legalMask}
            selectedKind={selectedKind}
            showCoordinates={coordinates}
            analysis={!store.isReplay && store.pending.size === 0 && store.analysis?.basisRevision === view.revision && store.analysis?.basisSemanticEventCursor === view.semanticEventCursor ? store.analysis : null}
            interactive={interactive}
            onPoint={onPoint}
            onPass={onPass}
          />
        ) : (
          <div className="no-board">
            {store.baselinePending ? "连接后等待权威快照…" : "尚无状态"}
          </div>
        )}
          <div className="board-toolbar"><ReplayBar store={store} />
            <button title="新对局" aria-label="新对局" onClick={props.onNewGame} disabled={!props.onNewGame || store.pending.size > 0}><Plus size={18} /></button>
            <label className="coordinate-toggle"><input type="checkbox" checked={coordinates} onChange={(event) => setCoordinates(event.target.checked)} />坐标</label>
          </div>
          {tab === "records" && <div className="move-timeline" aria-label="对局时间线">{store.history.map((entry, index) => <button key={index} aria-pressed={index === store.cursor} onClick={() => store.setCursor(index)}>{entry.revision}</button>)}</div>}
        </section>
        <aside className="control-panel">
          <section className="theme-panel"><h2><Settings2 size={15} />视觉主题</h2><div className="theme-swatches">
            {[{ id: "light", label: "明亮", Icon: Sun }, { id: "dark", label: "暗夜", Icon: Moon }, { id: "elegant", label: "典雅", Icon: Feather }, { id: "minimal", label: "简约", Icon: Disc }, { id: "cyber", label: "赛博", Icon: Cpu }].map(({ id, label, Icon }) => <button key={id} className={`theme-swatch ${id}`} title={label} aria-label={label + "主题"} aria-pressed={theme === id} onClick={() => changeTheme(id)}><Icon size={20} /></button>)}
          </div></section>
          <section className="moves-panel"><h2>落子</h2>
          <KindSelector selected={selectedKind} onSelect={setKind} remaining={remaining} disabled={!interactive || pendingDouble !== null} />
          <QuotaBar store={store} />
          </section>
          <section className="engine-panel"><h2><Cpu size={15} />引擎分析</h2>
          <div className="engine-switches">
            <label><input type="checkbox" role="switch" aria-label="加载模型" checked={modelBusy ? modelTarget : modelReady} disabled={!connected || modelBusy || store.pending.size > 0} onChange={(event) => { void toggleModel(event.target.checked); }} />加载模型<span>{modelBusy ? (modelTarget ? "加载中" : "卸载中") : modelReady ? "已加载" : "未加载"}</span></label>
            <label><input type="checkbox" role="switch" aria-label="持续分析" checked={autoAnalysis} disabled={!modelReady || modelBusy || !connected} onChange={(event) => setAutoAnalysis(event.target.checked)} />持续分析<span>{autoAnalysis ? "运行" : "暂停"}</span></label>
          </div>
          {modelError && <div role="alert" className="analysis-error">{modelError}</div>}
          <div className="analysis-settings"><label>计算量<input aria-label="分析计算量" type="number" min={1} max={65536} value={playouts} onChange={(event) => { const n = Number(event.target.value); if (Number.isInteger(n) && n > 0 && n <= 65536) setPlayouts(n); }} /></label></div>
          <AnalysisPanel
            store={store}
            canPlayChosen={currentAnalysisChoice(store) !== null}
            onPlayChosen={() => playAnalysisChoice(store, actions)}
          />
          </section>
          <SgfPanel store={store} onImport={(moves) => actions.startImport(moves)} />
          <ReceiptLog store={store} />
          {store.lastExport !== null && (
            <details className="export-log">
              <summary>SGF({store.lastExport.format})</summary>
              <pre>{store.lastExport.sgf}</pre>
            </details>
          )}
        </aside>
      </main>
      </div>
    </div>
  );
}
